import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Chip, LinearProgress, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Skeleton, IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getCampaignDetail, getCampaignProgress, getCampaignLeaderboard } from '../../api/incentiveApi';
import { useUser } from '../../context/userContext';

const PP = "'Poppins', sans-serif";
const API_BASE = process.env.REACT_APP_API_BASE_URL?.replace(/\/api$/, '') || '';

const TYPE_COLORS = {
  Case: { bg: '#EFF6FF', color: '#1A8CFF' },
  Travel: { bg: '#F5F3FF', color: '#8B5CF6' },
  Item: { bg: '#FFFBEB', color: '#F59E0B' },
};

const TIER_COLORS = ['#F59E0B', '#10B981', '#1A8CFF', '#8B5CF6', '#EF4444'];

const formatCurrency = (val) => {
  if (val == null) return 'RM 0';
  return `RM ${Number(val).toLocaleString('en-MY', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatNumber = (val) => {
  if (val == null) return '0';
  return Number(val).toLocaleString('en-MY');
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' });
};

const getInitials = (name) => {
  if (!name) return '??';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
};

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const [detail, setDetail] = useState(null);
  const [progress, setProgress] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [detailRes, progressRes, lbRes] = await Promise.allSettled([
          getCampaignDetail(id),
          getCampaignProgress(id),
          getCampaignLeaderboard(id),
        ]);
        if (detailRes.status === 'fulfilled') setDetail(detailRes.value.data);
        if (progressRes.status === 'fulfilled') setProgress(progressRes.value.data);
        if (lbRes.status === 'fulfilled') setLeaderboard(lbRes.value.data ?? []);
      } catch (err) {
        console.error('Error fetching campaign detail:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [id]);

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rounded" height={120} sx={{ borderRadius: '14px', mb: 3 }} />
        <Skeleton variant="rounded" height={200} sx={{ borderRadius: '14px', mb: 3 }} />
        <Skeleton variant="rounded" height={300} sx={{ borderRadius: '14px' }} />
      </Box>
    );
  }

  if (!detail) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography sx={{ color: '#94A3B8', fontFamily: PP }}>Campaign not found.</Typography>
      </Box>
    );
  }

  const typeStyle = TYPE_COLORS[detail.type] ?? TYPE_COLORS.Case;
  const isRevenue = detail.metric === 'Revenue';
  const isCase = !isRevenue; // Case-metric: tier RewardAmount is per-case rate
  const formatValue = (val) => isRevenue ? formatCurrency(val) : formatNumber(val);
  const isEnded = new Date(detail.endDate) < new Date();
  const embedUrl = getYouTubeEmbedUrl(detail.youTubeUrl);
  const sortedTiers = [...(detail.tiers || [])].sort((a, b) => a.sortOrder - b.sortOrder);

  // Earned RM calculation.
  // Case metric — BANDED: each tier defines an upper-bound band; cases in
  // that band earn at that tier's RewardAmount/case. Bands compose
  // cumulatively. Tier 1 band = [0, target_1]; tier i band = (target_(i-1),
  // target_i]. Cases above the top tier's target earn nothing.
  // Example: tiers (2, 0), (5, 100), (10, 150), (15, 200) at 20 cases:
  //   band1: 2 cases × 0 = 0
  //   band2: 3 cases × 100 = 300
  //   band3: 5 cases × 150 = 750
  //   band4: 5 cases × 200 = 1000
  //   total = 2050
  // Revenue metric: total = fixed RewardAmount of the highest tier reached
  // (no banding/multiplication; just the bonus for hitting that tier).
  // For case-metric incentives: split cases across non-zero-rate tiers.
  //   First non-zero tier covers cases [1 .. nextTier.target - 1]
  //   Each subsequent tier covers cases [tier.target .. nextTier.target - 1]
  //   Last tier covers cases [tier.target .. v]
  // Returns an array { tier, casesInBand, earned } per non-zero tier, in
  // ascending target order. Used by both computeEarned and per-tier display.
  const computeBandBreakdown = (v) => {
    const nonZero = [...sortedTiers]
      .filter(t => Number(t.rewardAmount ?? 0) > 0)
      .sort((a, b) => Number(a.target) - Number(b.target));
    const out = [];
    for (let i = 0; i < nonZero.length; i++) {
      const tier = nonZero[i];
      if (v < Number(tier.target)) break;
      const lower = i === 0 ? 1 : Number(tier.target);
      const upper = i + 1 < nonZero.length ? Number(nonZero[i + 1].target) - 1 : v;
      const cases = Math.max(0, Math.min(v, upper) - lower + 1);
      const rate = Number(tier.rewardAmount ?? 0);
      out.push({ tier, casesInBand: cases, earned: cases * rate });
    }
    return out;
  };

  const computeEarned = (raw) => {
    let v = Number(raw) || 0;
    if (v <= 0 || sortedTiers.length === 0) return 0;
    // Min cap: below this, no earnings.
    const minCap = detail?.minEligibility != null ? Number(detail.minEligibility) : null;
    if (minCap != null && v < minCap) return 0;
    // Max cap: anything above this doesn't earn extra.
    const maxCap = detail?.maxEligibility != null ? Number(detail.maxEligibility) : null;
    if (maxCap != null) v = Math.min(v, maxCap);
    if (isRevenue) {
      let bonus = 0;
      for (const t of sortedTiers) {
        if (v >= Number(t.target) && (t.rewardAmount ?? 0) > 0) bonus = Number(t.rewardAmount);
      }
      return bonus;
    }
    return computeBandBreakdown(v).reduce((sum, b) => sum + b.earned, 0);
  };

  // Display helper for a tier's reward in the summary row (the chip card
  // that used to render `Reward: <free text>`). Prefer the structured
  // RewardAmount when set; fall back to the free-text Reward field.
  const formatTierReward = (t) => {
    const amt = Number(t.rewardAmount ?? 0);
    if (amt > 0) {
      return isCase
        ? `${formatCurrency(amt)} / case`
        : formatCurrency(amt);
    }
    return t.reward || '—';
  };

  const headSx = { fontWeight: 600, fontSize: '0.72rem', color: '#64748B', fontFamily: PP, textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #E2E8F0', padding: '10px 14px', backgroundColor: '#F8FAFC' };
  const cellSx = { fontFamily: PP, fontSize: '0.82rem', color: '#374151', padding: '10px 14px', borderBottom: '1px solid #F1F5F9' };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('/incentives')} size="small" sx={{ color: '#64748B' }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A', fontFamily: PP }}>
              {detail.name}
            </Typography>
            <Chip label={detail.type} size="small" sx={{ backgroundColor: typeStyle.bg, color: typeStyle.color, fontWeight: 600, fontSize: '0.72rem', fontFamily: PP }} />
            {isEnded && <Chip label="Ended" size="small" sx={{ bgcolor: '#F1F5F9', color: '#64748B', fontWeight: 600, fontSize: '0.65rem', fontFamily: PP }} />}
          </Box>
          <Typography sx={{ fontSize: '0.82rem', color: '#64748B', fontFamily: PP }}>
            {formatDate(detail.startDate)} - {formatDate(detail.endDate)}
          </Typography>
        </Box>
      </Box>

      {/* Description with image + video + tier rewards */}
      <Card sx={{ borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          {detail.imageUrl && (
            <Box sx={{ mb: 2 }}>
              <img src={`${API_BASE}${detail.imageUrl}`} alt={detail.name} style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 10, objectFit: 'cover' }} />
            </Box>
          )}
          {embedUrl && (
            <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: '10px', overflow: 'hidden', mb: 2 }}>
              <iframe src={embedUrl} title="YouTube" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }} allowFullScreen />
            </Box>
          )}
          {/* Tier rewards summary */}
          {sortedTiers.length > 0 && (
            <>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#0F172A', fontFamily: PP, mb: 1 }}>
                Rewards
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {sortedTiers.map((t, i) => (
                  <Box key={t.tierId} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '8px 14px', borderRadius: '10px', border: `1.5px solid ${TIER_COLORS[i % TIER_COLORS.length]}20`, backgroundColor: `${TIER_COLORS[i % TIER_COLORS.length]}08` }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: TIER_COLORS[i % TIER_COLORS.length] }} />
                    <Box>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#0F172A', fontFamily: PP }}>{t.tierName}</Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: '#64748B', fontFamily: PP }}>
                        Target: {isRevenue ? formatCurrency(t.target) : t.target} · Reward: {formatTierReward(t)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* My Progress with tier milestone bar */}
      {progress && (
        <Card sx={{ borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0F172A', fontFamily: PP }}>
                My Progress
              </Typography>
              {progress.multiplierActive && (
                <Chip label={`${progress.currentMultiplier ?? 2}x Multiplier Active!`} size="small"
                  sx={{ backgroundColor: '#FFFBEB', color: '#D97706', fontWeight: 700, fontSize: '0.72rem', fontFamily: PP, border: '1px solid #FDE68A' }} />
              )}
            </Box>

            {/* Summary */}
            <Box sx={{ mb: 3, p: 2, backgroundColor: '#F8FAFC', borderRadius: '10px', display: 'flex', gap: { xs: 2, md: 3 }, flexWrap: 'wrap', justifyContent: { xs: 'space-between', md: 'flex-start' } }}>
              <Box>
                <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', fontFamily: PP, fontWeight: 600, textTransform: 'uppercase' }}>Own</Typography>
                <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#0F172A', fontFamily: PP }}>{formatValue(progress.ownTotal)}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', fontFamily: PP, fontWeight: 600, textTransform: 'uppercase' }}>Downline</Typography>
                <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#0F172A', fontFamily: PP }}>{formatValue(progress.downlineTotal)}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', fontFamily: PP, fontWeight: 600, textTransform: 'uppercase' }}>Grand Total</Typography>
                <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#10B981', fontFamily: PP }}>{formatValue(progress.grandTotal)}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', fontFamily: PP, fontWeight: 600, textTransform: 'uppercase' }}>
                  Earned {isCase ? '(per case)' : '(tier bonus)'}
                </Typography>
                <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#1A8CFF', fontFamily: PP }}>
                  {formatCurrency(computeEarned(progress.grandTotal))}
                </Typography>
              </Box>
            </Box>

            {/* Tier milestone bar — horizontal bar with tier circles */}
            {(progress.tiers || progress.tierProgress)?.length > 0 && (() => {
              const tiers = progress.tiers || progress.tierProgress;
              const maxTarget = Math.max(...tiers.map(t => t.target));
              const grandTotal = progress.grandTotal ?? 0;
              const overallPct = maxTarget > 0 ? Math.min(100, (grandTotal / maxTarget) * 100) : 0;
              const currentTier = [...tiers].reverse().find(t => t.achieved);

              const nextTier = tiers.find(t => !t.achieved);
              const remaining = nextTier ? Math.max(0, nextTier.target - grandTotal) : 0;
              const daysLeft = Math.max(0, Math.ceil((new Date(detail.endDate) - new Date()) / (1000 * 60 * 60 * 24)));

              return (
                <Box sx={{ mb: 2 }}>
                  {/* How much left towards next tier */}
                  <Box sx={{ textAlign: 'center', mb: 2, p: 2, backgroundColor: '#F0F9FF', borderRadius: '12px', border: '1px solid #BAE6FD' }}>
                    {nextTier ? (
                      <>
                        <Typography sx={{ fontSize: '0.75rem', color: '#64748B', fontFamily: PP }}>Next tier</Typography>
                        <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, color: '#1A8CFF', fontFamily: PP }}>{formatValue(remaining)}</Typography>
                        <Typography sx={{ fontSize: '0.78rem', color: '#475569', fontFamily: PP }}>more to reach <strong>{nextTier.tierName}</strong></Typography>
                      </>
                    ) : (
                      <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#10B981', fontFamily: PP }}>All tiers achieved! 🎉</Typography>
                    )}
                  </Box>

                  {/* Duration + days left */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 2, md: 3 }, mb: 3, flexWrap: 'wrap' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '0.65rem', color: '#94A3B8', fontFamily: PP, fontWeight: 600, textTransform: 'uppercase' }}>Duration</Typography>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#0F172A', fontFamily: PP }}>{formatDate(detail.startDate)} - {formatDate(detail.endDate)}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '0.65rem', color: '#94A3B8', fontFamily: PP, fontWeight: 600, textTransform: 'uppercase' }}>Remaining</Typography>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: isEnded ? '#EF4444' : '#F59E0B', fontFamily: PP }}>
                        {isEnded ? 'Ended' : `${daysLeft} days left`}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Horizontal bar with profile photo marker + tier milestones */}
                  <Box sx={{ position: 'relative', mx: { xs: 3, md: 5 }, mt: 7, mb: 10, minHeight: 80, pt: 4, pb: 4 }}>
                    {/* Background bar */}
                    <Box sx={{ height: 10, borderRadius: 5, backgroundColor: '#E2E8F0', position: 'relative' }}>
                      {/* Filled bar */}
                      <Box sx={{ height: '100%', borderRadius: 5, width: `${overallPct}%`,
                        background: 'linear-gradient(90deg, #1A8CFF, #10B981)',
                        transition: 'width 0.8s ease',
                      }} />
                      {/* Profile photo on the bar at progress position */}
                      <Box sx={{
                        position: 'absolute', top: -13, left: `${Math.max(2, Math.min(overallPct, 96))}%`, transform: 'translateX(-50%)',
                        width: 36, height: 36, borderRadius: '50%', border: '3px solid #1A8CFF',
                        backgroundColor: 'white', boxShadow: '0 2px 8px rgba(26,140,255,0.3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        overflow: 'hidden', zIndex: 2,
                      }}>
                        {user?.profilePhotoUrl ? (
                          <img src={`${API_BASE}${user.profilePhotoUrl}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#1A8CFF', fontFamily: PP }}>
                            {getInitials(user?.firstName || user?.alias)}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Tier milestone circles — alternate labels above/below */}
                    {tiers.map((tier, i) => {
                      const pos = maxTarget > 0 ? Math.max(3, Math.min(97, (tier.target / maxTarget) * 100)) : 3;
                      const achieved = tier.achieved;
                      const color = TIER_COLORS[i % TIER_COLORS.length];
                      const isAbove = i % 2 === 0;
                      return (
                        <Box key={i} sx={{ position: 'absolute', left: `${pos}%`, transform: 'translateX(-50%)', textAlign: 'center', zIndex: 1, top: 23 }}>
                          {/* Label above — hidden on mobile */}
                          {isAbove && (
                            <Box sx={{ position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', mb: 1, whiteSpace: 'nowrap', display: { xs: 'none', md: 'block' } }}>
                              <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: achieved ? color : '#64748B', fontFamily: PP }}>
                                {tier.tierName}
                              </Typography>
                              <Typography sx={{ fontSize: '0.5rem', color: '#94A3B8', fontFamily: PP }}>
                                {formatValue(tier.target)}
                              </Typography>
                            </Box>
                          )}
                          {/* Circle */}
                          <Box sx={{
                            width: 28, height: 28, borderRadius: '50%', border: `2.5px solid ${achieved ? color : '#CBD5E1'}`,
                            backgroundColor: achieved ? color : 'white', mx: 'auto',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: achieved ? `0 2px 6px ${color}40` : '0 1px 2px rgba(0,0,0,0.08)',
                          }}>
                            {achieved ? (
                              <Typography sx={{ color: 'white', fontSize: '0.6rem', fontWeight: 700 }}>✓</Typography>
                            ) : (
                              <Typography sx={{ color: '#CBD5E1', fontSize: '0.55rem', fontWeight: 700 }}>{i + 1}</Typography>
                            )}
                          </Box>
                          {/* Label below — hidden on mobile */}
                          {!isAbove && (
                            <Box sx={{ whiteSpace: 'nowrap', mt: 1, display: { xs: 'none', md: 'block' } }}>
                              <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: achieved ? color : '#64748B', fontFamily: PP }}>
                                {tier.tierName}
                              </Typography>
                              <Typography sx={{ fontSize: '0.5rem', color: '#94A3B8', fontFamily: PP }}>
                                {formatValue(tier.target)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Tier reward list */}
                  {(() => {
                    // Pre-compute the band breakdown once for the whole list
                    // so each tier row can look up its share. Apply min/max
                    // caps so the per-tier amounts match the total earned.
                    const minCap = detail?.minEligibility != null ? Number(detail.minEligibility) : null;
                    const maxCap = detail?.maxEligibility != null ? Number(detail.maxEligibility) : null;
                    let cappedTotal = grandTotal;
                    if (minCap != null && cappedTotal < minCap) cappedTotal = 0;
                    if (maxCap != null) cappedTotal = Math.min(cappedTotal, maxCap);
                    const breakdown = isRevenue ? null : computeBandBreakdown(cappedTotal);
                    return tiers.map((tier, i) => {
                      const tierRemaining = Math.max(0, tier.target - grandTotal);
                      let tierEarned = 0;
                      if (tier.achieved) {
                        if (isRevenue) {
                          tierEarned = Number(tier.rewardAmount ?? 0);
                        } else {
                          // Find this tier's band entry by tierId/tierName + target
                          const entry = breakdown && breakdown.find(b =>
                            b.tier.tierName === tier.tierName && Number(b.tier.target) === Number(tier.target));
                          tierEarned = entry ? entry.earned : 0;
                        }
                      }
                      return (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '12px 16px', mb: 1,
                        borderRadius: '12px',
                        border: tier.achieved ? `2px solid ${TIER_COLORS[i % TIER_COLORS.length]}` : '1px solid #E2E8F0',
                        background: tier.achieved ? `linear-gradient(135deg, ${TIER_COLORS[i % TIER_COLORS.length]}15, ${TIER_COLORS[i % TIER_COLORS.length]}05)` : '#FAFAFA',
                        boxShadow: tier.achieved ? `0 2px 8px ${TIER_COLORS[i % TIER_COLORS.length]}20` : 'none',
                        transition: 'all 0.3s',
                      }}>
                        <Box sx={{
                          width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                          background: tier.achieved ? `linear-gradient(135deg, ${TIER_COLORS[i % TIER_COLORS.length]}, ${TIER_COLORS[(i + 1) % TIER_COLORS.length]})` : '#E2E8F0',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          boxShadow: tier.achieved ? '0 2px 6px rgba(0,0,0,0.15)' : 'none',
                        }}>
                          <Typography sx={{ color: tier.achieved ? 'white' : '#94A3B8', fontSize: '0.75rem', fontWeight: 700 }}>
                            {tier.achieved ? '🏆' : i + 1}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, fontFamily: PP, color: tier.achieved ? TIER_COLORS[i % TIER_COLORS.length] : '#0F172A' }}>
                            {tier.tierName}
                          </Typography>
                          <Typography sx={{ fontSize: '0.72rem', color: tier.achieved ? '#475569' : '#94A3B8', fontFamily: PP }}>
                            {tier.reward}
                          </Typography>
                        </Box>
                        {tier.achieved ? (
                          <Box sx={{ textAlign: 'right' }}>
                            {tierEarned > 0 && (
                              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: TIER_COLORS[i % TIER_COLORS.length], fontFamily: PP }}>
                                {formatCurrency(tierEarned)}
                              </Typography>
                            )}
                            <Chip label="🎉 Achieved" size="small" sx={{
                              mt: tierEarned > 0 ? 0.25 : 0,
                              background: `linear-gradient(135deg, ${TIER_COLORS[i % TIER_COLORS.length]}, ${TIER_COLORS[(i + 1) % TIER_COLORS.length]})`,
                              color: 'white', fontWeight: 700, fontSize: '0.6rem', fontFamily: PP, height: 18,
                            }} />
                          </Box>
                        ) : (
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1A8CFF', fontFamily: PP }}>
                              {formatValue(tierRemaining)}
                            </Typography>
                            <Typography sx={{ fontSize: '0.6rem', color: '#94A3B8', fontFamily: PP }}>more to go</Typography>
                          </Box>
                        )}
                      </Box>
                    );
                    });
                  })()}
                </Box>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Leaderboard */}
      <Card sx={{ borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, mb: 2 }}>
            Leaderboard
          </Typography>

          {leaderboard.length === 0 ? (
            <Typography sx={{ color: '#94A3B8', fontFamily: PP, fontSize: '0.85rem', textAlign: 'center', py: 3 }}>
              No leaderboard data available yet.
            </Typography>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '10px', border: '1px solid #E2E8F0' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#F8FAFC' }}>
                    <TableCell sx={{ ...headSx, width: 50 }}>#</TableCell>
                    <TableCell sx={headSx}>Agent</TableCell>
                    <TableCell sx={headSx}>Group</TableCell>
                    <TableCell sx={{ ...headSx, textAlign: 'right' }}>Own Total</TableCell>
                    <TableCell sx={{ ...headSx, textAlign: 'right' }}>Downline</TableCell>
                    <TableCell sx={{ ...headSx, textAlign: 'right' }}>Grand Total</TableCell>
                    <TableCell sx={headSx}>Tier</TableCell>
                    <TableCell sx={{ ...headSx, textAlign: 'right' }}>Earned</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leaderboard.map((row, idx) => {
                    const isCurrentUser = String(row.agentId) === String(user?.userId);
                    return (
                      <TableRow key={idx} sx={{
                        backgroundColor: isCurrentUser ? '#EFF6FF' : 'transparent',
                        '&:hover': { backgroundColor: isCurrentUser ? '#DBEAFE' : '#F8FAFC' },
                      }}>
                        <TableCell sx={{ ...cellSx, fontWeight: 700, color: idx < 3 ? '#F59E0B' : '#94A3B8' }}>
                          {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : row.rank ?? idx + 1}
                        </TableCell>
                        <TableCell sx={cellSx}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {row.profilePhotoUrl ? (
                              <img src={`${API_BASE}${row.profilePhotoUrl}`} alt={row.agentName}
                                style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                            ) : (
                              <Box sx={{
                                width: 28, height: 28, borderRadius: '50%', backgroundColor: '#E0E7FF',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '0.6rem', fontWeight: 700, color: '#3730A3',
                              }}>
                                {getInitials(row.agentName)}
                              </Box>
                            )}
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: isCurrentUser ? 600 : 400, fontFamily: PP }}>
                              {row.agentName}{isCurrentUser ? ' (You)' : ''}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ ...cellSx, color: '#64748B' }}>{row.groupName ?? '-'}</TableCell>
                        <TableCell sx={{ ...cellSx, textAlign: 'right' }}>{formatValue(row.ownTotal)}</TableCell>
                        <TableCell sx={{ ...cellSx, textAlign: 'right' }}>{formatValue(row.downlineContribution)}</TableCell>
                        <TableCell sx={{ ...cellSx, textAlign: 'right', fontWeight: 600, color: '#0F172A' }}>{formatValue(row.grandTotal)}</TableCell>
                        <TableCell sx={cellSx}>
                          {row.tierAchieved ? (
                            <Chip label={row.tierAchieved} size="small" sx={{ backgroundColor: '#D1FAE5', color: '#065F46', fontWeight: 600, fontSize: '0.65rem', fontFamily: PP }} />
                          ) : '-'}
                        </TableCell>
                        <TableCell sx={{ ...cellSx, textAlign: 'right', fontWeight: 600, color: '#1A8CFF' }}>
                          {(() => {
                            const earned = computeEarned(row.grandTotal);
                            return earned > 0 ? formatCurrency(earned) : '-';
                          })()}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Description & Terms — at the bottom */}
      {detail.description && (
        <Card sx={{ borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', mt: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, mb: 1 }}>
              Description & Terms
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: '#475569', fontFamily: PP, whiteSpace: 'pre-line', lineHeight: 1.8 }}>
              {detail.description}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
